/**
 * Agreements — the single lifecycle tab for Hotel Supplier Management.
 *
 * One list with a stage pill-bar rather than separate Sourcing / Loading /
 * Renewals tabs: those are STAGES of the same agreement, separated for workload
 * rather than for meaning. Stages are derived server-side from
 * `Agreement.status` (see src/lib/hsp/agreement.ts in customer-management-api) —
 * never stored, so there is no second source of truth to desync.
 *
 * Column headers sort. The sort is applied SERVER-side (?sort=&dir=) rather than
 * in the browser, because the endpoint caps at 500 rows — sorting the fetched
 * page would show the wrong "highest rate" once the book of business outgrows
 * that cap. Rate and Loads headers are inert: they are aggregates over child
 * rows, not agreement columns, so the API cannot order by them yet.
 *
 * Every row EXPANDS rather than the table growing more columns. The team needs
 * the GDS codes, the hotel's phone, both contacts, the negotiated term and the
 * full rate / load / blackout detail — roughly thirty fields. Twelve columns is
 * already at the edge of scannable, so the columns stay the ones you sort and
 * compare on, and everything else lives one disclosure click away in the row's
 * own detail panel. No navigation, no modal, no losing your place in the queue.
 *
 * Read-only for now. The write surface (start an agreement, advance the
 * lifecycle, record a rate, work a load row) follows on a detail route.
 */
import { Link, useSearchParams } from 'react-router';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import {
  commitDelay,
  committedQuery,
  searchHint,
} from '@/features/hotel-supplier/lib/search-query';
import {
  DetailRow,
  DisclosureCell,
  DisclosureHeaderCell,
  TableScroll,
  detailRowId,
  totalColumns,
  useExpandedRows,
  zebraFor,
} from '@/features/crm/components/ExpandableRow';
import { AgreementDetail, fmtDate, type Agreement } from './AgreementDetail';
import { CustomerPicker } from '../../components/CustomerPicker';
import { LogContactForm } from '../../components/LogContactForm';
import { LogRateTestForm } from '../../components/LogRateTestForm';
import { StatusControl } from '../../components/StatusControl';
import {
  agreementsCsv,
  csvFilename,
  exportSummary,
} from '../../lib/agreements-csv';
import { useMe } from '@/features/crm/auth/use-capabilities';

/**
 * Upper bound on customers in one filter. Mirrors MAX_CUSTOMER_IDS in
 * customer-management-api/src/lib/hsp/agreement.ts, where it is enforced with a
 * 400 — this copy exists so the picker refuses the 51st pick locally instead of
 * letting the user build a selection the server will reject wholesale.
 */
const MAX_CUSTOMERS = 50;

/**
 * Split `?customer_id=` into ids. The server-side twin is `parseCustomerIds`; the
 * contract that matters in both is that an absent or blank value yields an EMPTY
 * list meaning "no customer filter", never a one-element list containing ''.
 */
function parseCustomerIds(raw: string | null): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    const id = part.trim();
    if (id) seen.add(id);
  }
  return [...seen];
}

const STAGES = [
  { id: 'sourcing', label: 'Sourcing' },
  { id: 'loading', label: 'Loading' },
  { id: 'renewal', label: 'Renewal' },
  { id: 'active', label: 'Active' },
  { id: 'closed', label: 'Closed' },
] as const;

/**
 * The table's columns. `sort` is the API's `?sort=` value; a column with no
 * `sort` renders as a plain header that does nothing when clicked, which is
 * honest about the aggregate columns the API cannot order by.
 *
 * City and State are SEPARATE columns rather than one "Dallas, TX" cell: they
 * answer different questions ("everything in this city" vs a regional book of
 * business) and a combined cell can only be sorted one of the two ways.
 *
 * Exported so AgreementsPage.spec.ts can assert every sortable column here is
 * one the API actually accepts — a typo would otherwise surface as a silent 400
 * only when a user clicked that header.
 */
export interface AgreementColumn {
  key: string;
  label: string;
  /** The API's ?sort= value, or null for aggregate columns it cannot order by. */
  sort: string | null;
  /**
   * The direction a FIRST click applies, when descending is the wrong default.
   *
   * Only "last contacted date" wants this so far, and it wants it badly: the
   * column exists to answer "who has been waiting longest", so a first click
   * that shows the most recently contacted rows is the exact opposite of the
   * question. "I want it to be able to be sorted by last contact date, so the
   * oldest contacted can come up first."
   */
  defaultDir?: 'asc' | 'desc';
  align?: 'right';
  /** Rendered in the monospace cell class — codes, not prose. */
  mono?: boolean;
}

export const AGREEMENT_COLUMNS: AgreementColumn[] = [
  { key: 'hotel', label: 'Hotel', sort: 'hotel' },
  { key: 'city', label: 'City', sort: 'city' },
  { key: 'state', label: 'State', sort: 'state' },
  // Chain removed 2026-08-11 (Stacie), pairing with Brand coming off the Hotels
  // table in the same batch — the chain dimension is consistently not how this
  // work is done. `chain` stays in the API's sort whitelist so a bookmarked
  // ?sort=chain does not 400; this page already drops sort values it has no
  // column for, so such a link degrades to the default order instead.
  { key: 'customer', label: 'Customer', sort: 'customer' },
  { key: 'tmc', label: 'TMC', sort: 'tmc' },
  { key: 'status', label: 'Status', sort: 'status' },
  /*
   * "Owner" HAS TWO MEANINGS, and only one of them belongs in a header row.
   *
   * Jeff, 2026-09-03: "Instead of showing 'owner' in the header row, we want
   * two columns: 'Last contacted by' that is the user that saved that most
   * recent contact; 'Last contacted date' the date of the last saved contact.
   * You can keep owner defined as it is today, we may need to use that value
   * later on during the workflow build."
   *
   * Stacie, on the column it replaces: "I don't want that. I want last
   * contacted with this agreement and date." `ownerTeam` answers "which team
   * holds this", which is Supplier Relations for every row on the tab and so
   * carries no information; who last touched it and when is the actual work
   * signal. `ownerTeam` stays on the interface and in the API — it is wanted
   * for the workflow build, just not as a column.
   */
  /*
   * SORTABLE HERE TOO, matching the Loading tab.
   *
   * These were `sort: null` on every stage while the API could not order by
   * them at all. Migration 20260903190000 denormalised both onto the agreement,
   * so the only reason left to hold them here would be that the chase signal
   * matters less outside Loading — which is a guess, and a column that sorts on
   * one tab and not another reads as a bug rather than as a judgement.
   *
   * No `defaultDir: 'asc'` outside Loading: oldest-first is right for a to-do
   * list, and Sourcing/Active/Closed are not to-do lists.
   */
  {
    key: 'last_contact_by',
    label: 'Last contacted by',
    sort: 'last_contact_by',
  },
  {
    key: 'last_contact_at',
    label: 'Last contacted date',
    sort: 'last_contact',
  },
  { key: 'hsm', label: 'Hotel contact', sort: null },
  { key: 'sabre', label: 'Sabre', sort: null },
  { key: 'rate', label: 'Rate', sort: null, align: 'right' },
  { key: 'term_end', label: 'Term ends', sort: 'term_end' },
  { key: 'loads', label: 'Loads', sort: null },
];

/**
 * Customer-enrollment filter. Asked for on the Loading queue — "hotels for Supplier
 * Mgmt customers versus not" — but offered on every stage because the question is
 * the same everywhere.
 *
 * Worth being clear about what this is: the enrollment flag used as a FILTER, not
 * as a visibility gate. Removing it as a gate (issue #1694) is what makes every
 * customer visible to every internal user; this chip lets you narrow to a subset
 * and clear it again. Those are compatible, not contradictory.
 */
const ENROLLMENT_FILTERS = [
  { id: '', label: 'All customers' },
  { id: 'true', label: 'Supplier Management' },
  { id: 'false', label: 'Not enrolled' },
] as const;

/** Data columns plus the leading disclosure column, for the detail row's colSpan. */
/**
 * The columns each STAGE shows, because the stages are different jobs.
 *
 * Stacie, 2026-08-31, on why one column set cannot serve all four: "the headers
 * will be different from sourcing, loading, renewal, active. So for loading
 * specifically, these are the headers that we talked about."
 *
 * LOADING is the one she specified in full, and it is the tab Omar and Tyler are
 * meant to live in. What came off it, in her words:
 *   - TMC and Status — "those are kind of duh" (status doubly so: every row on
 *     this tab is `loading`, so the column carries no information)
 *   - Sabre ID, Rate, Term ends, Loads — "I don't know that I need Saber RateX
 *     or the Saber property ID, the rate, determines, or the loads. I think keep
 *     it very concise so that they can see at a glance."
 * And what went on: the person who contacted last, when, how, the loading
 * sub-status, and the accepted date.
 *
 * The other three stages keep the full set until she specifies them.
 */
/** Every sort value any stage offers. A bookmarked ?sort= stays valid across
 *  tabs; the page already drops a sort it has no column for. */
const ALL_SORTS: Set<string> = new Set();

const LOADING_COLUMNS: AgreementColumn[] = [
  { key: 'hotel', label: 'Hotel', sort: 'hotel' },
  { key: 'customer', label: 'Customer', sort: 'customer' },
  { key: 'city', label: 'City', sort: 'city' },
  { key: 'state', label: 'State', sort: 'state' },
  /*
   * BOTH SORT NOW. They were held — rendered as plain headers — for two days
   * because `SORTABLE_AGREEMENT_COLUMNS` could not express "the newest row of a
   * to-many", so a clickable header worked against the dev-mock and 400d in
   * production.
   *
   * Migration 20260903190000 denormalised the answer onto
   * `agreement.last_contact_{at,by}`, and a scalar sorts. This is the ask it was
   * built for: "I want it to be able to be sorted by last contact date, so the
   * oldest contacted can come up first."
   *
   * OLDEST FIRST is the default for the date, which is the whole point of the
   * column on this tab — it is a to-do list, so the least recently touched has
   * to surface. Never-contacted sorts ahead of any date, which the API applies
   * as NULLS FIRST rather than leaving to Postgres's default of last.
   */
  {
    key: 'last_contact_by',
    label: 'Last contacted by',
    sort: 'last_contact_by',
  },
  {
    key: 'last_contact_at',
    label: 'Last contacted date',
    sort: 'last_contact',
    defaultDir: 'asc',
  },
  // Still no sort: `last_contact_method` is denormalised for DISPLAY only.
  // Nobody asked to order a queue by how the last call was made, and adding it
  // would be inventing a feature to complete a symmetry.
  { key: 'contact_method', label: 'Method', sort: null },
  { key: 'substatus', label: 'Load state', sort: null },
  // `accepted` IS sortable — acceptedAt is an agreement scalar, so it is added
  // to the API whitelist in this change rather than held.
  { key: 'accepted', label: 'Accepted', sort: 'accepted' },
  { key: 'hsm', label: 'Hotel contact', sort: null },
];

/**
 * RENEWAL. Its own job again: this is a decision queue, not a chase queue.
 *
 * Stacie, 2026-08-31: an active agreement whose term ends within 120 days shows
 * up here so renewals are assumed rather than hand-created, and each row wants
 * "a status to say dismiss or renew or something… dismiss or send renewal."
 *
 * Sorted by term end, soonest first, because that is the urgency.
 */
const RENEWAL_COLUMNS: AgreementColumn[] = [
  { key: 'hotel', label: 'Hotel', sort: 'hotel' },
  { key: 'customer', label: 'Customer', sort: 'customer' },
  { key: 'city', label: 'City', sort: 'city' },
  { key: 'term_end', label: 'Term ends', sort: 'term_end' },
  { key: 'rate', label: 'Rate', sort: null, align: 'right' },
  { key: 'renewal_action', label: 'Decision', sort: null },
];

const COLUMNS_BY_STAGE: Record<string, AgreementColumn[]> = {
  loading: LOADING_COLUMNS,
  renewal: RENEWAL_COLUMNS,
};

const columnsForStage = (stage: string): AgreementColumn[] =>
  COLUMNS_BY_STAGE[stage] ?? AGREEMENT_COLUMNS;

for (const set of [AGREEMENT_COLUMNS, LOADING_COLUMNS, RENEWAL_COLUMNS]) {
  for (const c of set) if (c.sort) ALL_SORTS.add(c.sort);
}

/** An email is not a name. Show the local part until the API sends a display
 *  name, rather than a column of addresses nobody reads. */
/** An email under a name: shown in full, copied on click, and it says which it
 *  did. A mailto would open a blank draft, which is the rarer need. */
function CopyableEmail({ email, testId }: { email: string; testId: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="cell-sub"
      data-testid={testId}
      onClick={(e) => {
        // The row itself is not clickable, but the cell hosts a filter control —
        // don't let a copy also toggle that.
        e.stopPropagation();
        void navigator.clipboard
          ?.writeText(email)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
          // A clipboard denied by permissions must not look like a copy.
          .catch(() => setCopied(false));
      }}
      style={{
        background: 'none',
        border: 0,
        padding: 0,
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
      }}
      title={`Copy ${email}`}
      type="button"
    >
      {copied ? 'copied' : email}
    </button>
  );
}

/**
 * The two decisions a renewal row exists to capture.
 *
 * Stacie, 2026-08-31: "a status to say dismiss or renew or something, or dismiss
 * or send" — settling on "dismiss or send renewal". Both are real outcomes: not
 * every expiring rate is worth renewing, and saying so is the point of working
 * the queue rather than renewing by default.
 *
 * Send renewal reuses the EXISTING successor endpoint, which already knows how
 * to carry terms forward and refuses a commit that skips its own warnings. This
 * adds a decision surface, not a second renewal mechanism.
 */
function RenewalDecision({
  agreementId,
  onDone,
}: {
  agreementId: string;
  onDone: () => void;
}) {
  const { can } = useMe();
  const [busy, setBusy] = useState<null | 'dismiss' | 'send'>(null);
  const [error, setError] = useState<string | null>(null);

  if (!can('write:supplier')) {
    return <span className="cell-sub">read only</span>;
  }

  async function act(kind: 'dismiss' | 'send') {
    setBusy(kind);
    setError(null);
    try {
      if (kind === 'dismiss') {
        await apiFetch(`/api/btp/agreements/${agreementId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: 'dismissed',
            reason: 'not renewing',
          }),
        });
      } else {
        await apiFetch(`/api/btp/agreements/${agreementId}/renew`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
      }
      onDone();
    } catch (e) {
      // Named, not swallowed: a decision that silently failed is worse than
      // one that never happened, because the reader believes it landed.
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <button
        className="btn btn-sm"
        data-testid={`supplier-renewal-send-${agreementId}`}
        disabled={busy !== null}
        onClick={() => void act('send')}
        type="button"
      >
        {busy === 'send' ? 'Sending…' : 'Send renewal'}
      </button>
      <button
        className="btn btn-sm btn-quiet"
        data-testid={`supplier-renewal-dismiss-${agreementId}`}
        disabled={busy !== null}
        onClick={() => void act('dismiss')}
        type="button"
      >
        {busy === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
      </button>
      {error && (
        <span
          className="cell-sub"
          data-testid={`supplier-renewal-error-${agreementId}`}
          style={{ color: 'var(--danger, #b00)' }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * When the renewal decision is due — the inner threshold inside the queue.
 *
 * Mirrors RENEWAL_DECISION_DAYS in customer-management-api's lib/hsp/agreement.
 * Stacie, 2026-09-04: "renewals generated for review at 120 days and decisions
 * made closer to 90 days so we can start working on them." The queue opens at
 * 120 and this is the point at which a row stops being something to see and
 * becomes something to do.
 */
const RENEWAL_DECISION_DAYS = 90;

/**
 * Days until the term ends, DERIVED IN THE BROWSER from `termEnd`.
 *
 * The dev-mock sends a `daysToTermEnd` field; cm-api does not. So the countdown
 * worked on a laptop and rendered nothing against the real API — the same
 * asymmetry class as the sort headers that 400d. `termEnd` is sent by BOTH, so
 * computing from it is the version that works everywhere, and the harness field
 * is preferred only when present so nothing regresses locally.
 *
 * CALENDAR DAYS, computed between two UTC midnights — not elapsed milliseconds
 * floored, which was the first version and was wrong by one for most of the day.
 *
 * `termEnd` is a Postgres DATE and arrives as 'YYYY-MM-DD', which `Date.parse`
 * reads as UTC midnight. Subtracting `Date.now()` at, say, 2pm and flooring
 * therefore returns 59 for a term ending in 60 days — an off-by-one that
 * appears and disappears depending on the time of day the page is opened, and
 * that a reader counting days on a calendar would notice and not trust.
 *
 * Both sides are normalised to UTC midnight so the answer is the difference a
 * person would get from a calendar, and is stable across the day and across DST.
 */
function daysToTermEndOf(a: {
  termEnd?: string | null;
  daysToTermEnd?: number | null;
}): number | null {
  if (typeof a.daysToTermEnd === 'number') return a.daysToTermEnd;
  if (!a.termEnd) return null;
  const end = Date.parse(a.termEnd);
  if (Number.isNaN(end)) return null;
  const now = new Date();
  const todayUtcMidnight = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  return Math.round((end - todayUtcMidnight) / 86_400_000);
}

function personOf(who: string | null): string {
  if (!who) return '—';
  return who.includes('@') ? (who.split('@')[0] ?? who) : who;
}

/** The load state carries the same weight as a status, so it gets the same
 *  visual vocabulary: green for the one passing result, red for the rest. */
function substatusBadge(v: string): string {
  if (v === 'as_agreed' || v === 'loaded') return 'badge badge-green';
  if (v === 'testing') return 'badge badge-blue';
  return 'badge badge-red';
}

export default function AgreementsPage() {
  // Stage lives in the URL, not component state: these filters REPLACED the
  // Sourcing and Renewals tabs, so they have to stay linkable the way those tabs
  // were (bookmarks, the work-queue's deep links, sharing a queue with a
  // colleague). An unrecognised ?stage= falls back to sourcing.
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('stage') ?? '';
  const stage = STAGES.some((s) => s.id === raw) ? raw : 'sourcing';

  // Sort and the contact filter live in the URL alongside stage, for the same
  // reason: a queue you can send to a colleague. Anything not recognised is
  // dropped rather than passed through to the API.
  const sortRaw = searchParams.get('sort');
  const sort = ALL_SORTS.has(sortRaw ?? '') ? sortRaw : null;
  const dir = searchParams.get('dir') === 'asc' ? 'asc' : 'desc';
  const hsmEmail = searchParams.get('hsm_email');
  // Scope to one or MORE customers. Arrived 2026-08-09 with the Customers panel's
  // "Agreements for this customer" link — the endpoint already accepted
  // ?customer_id=, but this page dropped it, so such a link would have silently
  // listed every customer's agreements instead of that one's. Widened to a
  // comma-separated list the same day for Stacie's picker; a single id is still a
  // valid value, so every link already in the wild keeps working.
  const customerIdParam = searchParams.get('customer_id');
  const customerIds = parseCustomerIds(customerIdParam);
  // Stable primitive for the fetch effect's dep list. Depending on the array
  // itself would refetch on every render, since parseCustomerIds returns a fresh
  // one — a refetch loop, not a lint nit.
  const customerKey = customerIds.join(',');
  // Tri-state. Anything the API would reject is dropped here rather than
  // forwarded, same as ?sort= — a stale link must not strand the user on a 400.
  const smRaw = searchParams.get('supplier_managed');
  const supplierManaged = smRaw === 'true' || smRaw === 'false' ? smRaw : null;

  /*
   * A REF TO THE LIVE PARAMS, read at commit time rather than at render time.
   *
   * The search box commits on a timer now, so its `patchParams` call runs from
   * a closure created 250ms earlier. Merging into the `searchParams` captured
   * in that closure carries whatever was on screen when typing started — so
   * tapping a stage pill mid-search was silently undone by the search's own
   * commit. Measured: type, click Loading, and the next fetch went back to
   * `stage=sourcing&q=…`.
   *
   * `setSearchParams`'s FUNCTIONAL form does not fix this — tried first, and it
   * reverted the stage just the same, because react-router resolves that
   * updater against the params of the render that produced the setter. A ref
   * is independent of that and cannot go stale.
   *
   * Every filter on this toolbar goes through here, so they all get it.
   */
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;

  const patchParams = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(paramsRef.current);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) merged.delete(k);
      else merged.set(k, v);
    }
    setSearchParams(merged, { replace: true });
  };

  const setStage = (next: string) => patchParams({ stage: next });

  /**
   * Click a sortable header: the same column toggles direction, a new column
   * starts at ITS OWN default — descending for most, ascending for a column
   * whose useful end is the old one.
   */
  const toggleSort = (col: string, firstDir: 'asc' | 'desc' = 'desc') =>
    patchParams(
      sort === col
        ? { sort: col, dir: dir === 'asc' ? 'desc' : 'asc' }
        : { sort: col, dir: firstDir },
    );

  const [agreements, setAgreements] = useState<Agreement[]>([]);
  // How many rows the filter matches server-side, which can exceed what was
  // fetched. Null until a response arrives — and treated as "unknown", never as
  // "equal to what we have", because the export's completeness claim depends on
  // it. See exportSummary in ../../lib/agreements-csv.
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Which rows are expanded. Shared with the Hotels and Customers tables — see
  // features/crm/components/ExpandableRow for why this is component state rather
  // than URL state, and why the stripe parity is data-derived.
  //
  // `collapseAll` is destructured so the fetch effect below can depend on a
  // STABLE reference. Depending on `expanded` itself would re-run the effect on
  // every render — the hook returns a fresh object each time — and that is a
  // refetch loop, not a lint nit.
  const expanded = useExpandedRows();
  const { collapseAll } = expanded;

  // Bumped when something inside a row's panel may have moved the agreement —
  // a passing rate audit advances loading -> active, which changes which rows
  // belong on this tab at all.
  const { me } = useMe();
  const [reloadKey, setReloadKey] = useState(0);
  const q = searchParams.get('q') ?? '';
  const contactedBy = searchParams.get('contacted_by');

  /*
   * THE BOX HOLDS ITS OWN TEXT, and the URL holds the committed query.
   *
   * Bound straight to `?q=`, every character had to round-trip through the
   * router before it could appear, and typing faster than that round trip let
   * the controlled value overwrite what had just been typed — Jeff, 2026-09-11:
   * "it misses some of the first few letters if you're typing quickly". Local
   * state cannot lag the keyboard.
   *
   * Seeded from the URL so a shared or bookmarked link still shows its own
   * query in the box.
   */
  const [searchText, setSearchText] = useState(q);

  useEffect(() => {
    const next = committedQuery(searchText);
    // Already committed — nothing to do, and re-committing would restart the
    // fetch (and collapse every open row) for no change.
    if (next === (q || null)) return;
    const t = setTimeout(() => patchParams({ q: next }), commitDelay(next));
    return () => clearTimeout(t);
    // `patchParams` is recreated every render and is stable in behaviour; the
    // functional updater above is what makes leaving it out safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ stage });
    if (sort) {
      qs.set('sort', sort);
      qs.set('dir', dir);
    }
    if (hsmEmail) qs.set('hsm_email', hsmEmail);
    // The worklist axes. Search is what she asked for after clicking a couple of
    // rows — "where you get search would be good" — and contacted_by is the
    // per-person to-do list: "if I'm Tyler, I need to be able to come in here
    // and… pick anything that I contacted last."
    if (q) qs.set('q', q);
    if (contactedBy) qs.set('contacted_by', contactedBy);
    if (customerKey) qs.set('customer_id', customerKey);
    if (supplierManaged) qs.set('supplier_managed', supplierManaged);
    apiFetch<{ agreements: Agreement[]; total?: number }>(
      `/api/btp/agreements?${qs}`,
    )
      .then((d) => {
        if (!cancelled) {
          setAgreements(d.agreements);
          // `?? null` rather than `?? d.agreements.length`: an API that did not
          // send a total has not told us the page is complete, and inventing one
          // that agrees with the page is exactly how a truncated export ends up
          // labelled complete.
          setTotal(d.total ?? null);
          // Collapse everything on a re-query. The ids in the new result set are
          // usually different rows, and keeping stale ids expanded would open
          // whichever unrelated agreement happened to reuse one.
          collapseAll();
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    stage,
    sort,
    dir,
    hsmEmail,
    customerKey,
    supplierManaged,
    collapseAll,
    reloadKey,
    q,
    contactedBy,
  ]);

  const stageLabel = STAGES.find((s) => s.id === stage)?.label ?? stage;
  // Per-stage, so the Loading tab is the concise worklist Stacie specified while
  // the other three keep the full set until she specifies them.
  const columns = columnsForStage(stage);
  const totalCols = totalColumns(columns.length);

  // Customer names harvested from the rows we already have, so a chip for an id
  // that arrived through the URL reads as a name rather than a raw uuid. Free —
  // every agreement row already carries `customer.name`.
  const nameHints = useMemo(() => {
    const map: Record<string, string> = {};
    for (const a of agreements) {
      if (a.customer?.name) map[a.customerId] = a.customer.name;
    }
    return map;
  }, [agreements]);

  const setCustomerIds = (ids: string[]) =>
    patchParams({ customer_id: ids.length > 0 ? ids.join(',') : null });

  const summary = exportSummary(agreements, total);

  /**
   * Write the CSV out as a download.
   *
   * A Blob + object URL rather than a `data:` URI: Safari caps data-URI downloads
   * at a couple of megabytes and a 500-row export with the full rate detail
   * clears that, so the naive version works everywhere until the file matters.
   * The BOM is what makes Excel read it as UTF-8 — without it, an accented hotel
   * name arrives mojibaked.
   */
  const downloadCsv = () => {
    const csv = agreementsCsv(agreements);
    const blob = new Blob(['﻿', csv], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = csvFilename(stage, customerIds.length);
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoked on the next tick, not immediately: the click is asynchronous and
    // revoking synchronously can cancel the download it was created for.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div className="page" data-testid="supplier-agreements-page">
      {/*
       * TITLE AND ACTION SHARE A ROW, and the intro is one line.
       *
       * This block was 170px of a 720px chrome stack — an h1, a three-line
       * paragraph and a button, each on its own row, using about two thirds of
       * the width. The paragraph is onboarding prose that is read once and then
       * costs 60px on every visit forever; what it actually needed to say is
       * "pick a stage, open a row", which fits on one line beside nothing.
       */}
      <div
        className="page-header"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1>Agreements</h1>
          <p className="page-intro" style={{ margin: 0 }}>
            {/* ONE line. Two lines of onboarding prose cost 22px of table on
                every visit, forever, and this says the same thing. */}
            Every agreement, at any point of its life. Pick a stage to work that
            queue; open a row for the detail.
          </p>
        </div>
        {/*
         * TWO WAYS IN, and the bulk one is the outline button.
         *
         * Import is the rarer gesture and the far more consequential one — it
         * creates and updates in the thousands — so it does not compete with
         * the everyday action for the primary slot. It is still HERE rather
         * than behind an admin screen, because the person who has the export
         * is the person working this queue.
         */}
        <div style={{ display: 'flex', flexShrink: 0, gap: 8 }}>
          <Link
            className="btn btn-outline"
            data-testid="supplier-agreements-import-link"
            to="/hotel-supplier/agreements/import"
          >
            Import from Smartsheet
          </Link>
          <Link
            className="btn btn-primary"
            data-testid="supplier-agreements-new-link"
            to="/hotel-supplier/agreements/new"
          >
            Start an agreement
          </Link>
        </div>
      </div>

      <div className="section">
        {/*
         * THE STAGE PILLS LIVE IN THE BAR, and the bar's own label is gone.
         *
         * They were a separate 50px row directly under a bar whose label said
         * the same thing the active pill says — the stage, twice, on two rows.
         * Measured on the real stylesheet at 1440px: folding them in, merging
         * the filter rows below, and moving the row-count into the meta slot
         * takes the chrome above the first agreement from 581px to 366px. Jeff
         * asked for the width to be used; this is where the width was.
         */}
        <div className="section-header" style={{ flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              flex: '1 1 auto',
            }}
          >
            {STAGES.map((s) => (
              <button
                className={'pill' + (stage === s.id ? ' active' : '')}
                data-active={stage === s.id ? 'true' : 'false'}
                data-testid={`supplier-agreements-stage-${s.id}`}
                key={s.id}
                onClick={() => setStage(s.id)}
                type="button"
              >
                {s.label}
              </button>
            ))}
          </div>
          {/* `stageLabel` still drives the accessible name so the section is
              identifiable to a screen reader and to the tests, even though the
              active pill is what a sighted reader goes by. */}
          <span className="section-meta" data-testid="supplier-agreements-meta">
            <span className="sr-only">{stageLabel}. </span>
            {loading
              ? 'Refreshing…'
              : `${agreements.length.toLocaleString()} agreement${agreements.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className="section-body">
          {/* Second-level filter. Deliberately a visible chip row rather than a
              dropdown: the team asked to SEE the split, and a collapsed select
              hides which subset you are currently looking at. */}
          {/* THE WORKLIST CONTROLS — the first thing she asked for, before any
              clicking: search, and a per-person to-do filter.

              "We would need very first thing is even not even clicking into it,
              but having the ability to filter by the ones that I reached out to
              last. So if I'm Tyler, I need to be able to come in here and not
              just pick a customer, but pick anything that I contacted last."

              Rendered on every stage, not just Loading: sourcing has the same
              chase problem, and Julianne works it the same way. */}
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <input
              aria-label="Search agreements"
              className="input"
              data-testid="supplier-agreements-search"
              /*
               * NO `.trim()` HERE. Trimming on every keystroke meant a space
               * could never be typed: on "Cordera Suites" the space was
               * stripped the instant it was entered, so the box silently
               * refused the word break. Trimming belongs at commit time, which
               * is where `committedQuery` does it.
               */
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search hotel, customer, city, contact, load state…"
              /*
               * `flex`, NOT a min-width alone. `.crm-scope .input` is
               * `width: 100%`, so this input claimed the entire row and forced
               * every filter after it to wrap — which is why the toolbar read
               * as five stacked full-width rows using a third of the width
               * each, and why ~720px of chrome stood between the tab bar and
               * the first agreement (Jeff, 2026-09-09: "by the time you get to
               * the actual agreement portion of the tool there's no more screen
               * real estate"). The row already said `flexWrap: 'wrap'`; it just
               * never had the chance to use it.
               */
              style={{ flex: '1 1 320px', minWidth: 240, width: 'auto' }}
              type="search"
              value={searchText}
            />
            {searchHint(searchText) && (
              <span
                className="muted"
                data-testid="supplier-agreements-search-hint"
                style={{ fontSize: 11.5 }}
              >
                {searchHint(searchText)}
              </span>
            )}
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                margin: '0 2px 0 8px',
              }}
            >
              Contacted by
            </span>
            {[
              { id: null, label: 'Anyone', held: false },
              /*
               * "ME" WORKS NOW. It was disabled-with-a-reason while the route
               * answered 400 to anything but `__none__`, because "who contacted
               * it last" was not expressible in a Prisma `where`.
               *
               * `agreement.last_contact_by` (migration 20260903190000) makes it
               * an equality filter, which is what Stacie asked for: "if I'm
               * Tyler, I need to be able to come in here and not just pick a
               * customer, but pick anything that I contacted last."
               */
              { id: me?.email ?? '', label: 'Me', held: false },
              // Never contacted is the most overdue thing on the queue, so it
              // gets its own filter rather than hiding at one end of a sort.
              { id: '__none__', label: 'Never contacted', held: false },
            ].map((f) => {
              const active = (contactedBy ?? null) === (f.id || null);
              return (
                <button
                  className={'pill' + (active ? ' active' : '')}
                  data-active={active ? 'true' : 'false'}
                  data-held={f.held ? 'true' : 'false'}
                  data-testid={`supplier-agreements-contacted-${f.id === null ? 'anyone' : f.id === '__none__' ? 'never' : 'me'}`}
                  disabled={f.held || (f.label === 'Me' && !me?.email)}
                  key={f.label}
                  onClick={() => patchParams({ contacted_by: f.id || null })}
                  title={
                    f.held
                      ? 'Filtering to a person needs the last-contact column — coming with the contact-model migration.'
                      : undefined
                  }
                  type="button"
                >
                  {f.label}
                </button>
              );
            })}
            {/*
             * CUSTOMER RIDES IN THE SAME ROW as search and Contacted by, rather
             * than opening a third one. Each of these rows used about a third of
             * the width and a full 44-56px of height; the row already wraps, so
             * on a narrow pane they still stack — they just stop stacking on a
             * wide one. Measured: 581px → 366px of chrome above the first
             * agreement, with the pills folded into the bar above.
             */}
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                marginRight: 2,
                marginLeft: 8,
              }}
            >
              Customer
            </span>
            {ENROLLMENT_FILTERS.map((f) => {
              const active = (supplierManaged ?? '') === f.id;
              return (
                <button
                  className={'pill' + (active ? ' active' : '')}
                  data-active={active ? 'true' : 'false'}
                  data-testid={`supplier-agreements-enrollment-${f.id || 'all'}`}
                  key={f.id || 'all'}
                  onClick={() =>
                    patchParams({ supplier_managed: f.id === '' ? null : f.id })
                  }
                  type="button"
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          {hsmEmail && (
            <div
              data-testid="supplier-agreements-contact-filter"
              style={{ marginBottom: 12 }}
            >
              <span className="badge badge-blue">
                Contact: {hsmEmail}{' '}
                <button
                  aria-label="Clear contact filter"
                  className="row-link"
                  onClick={() => patchParams({ hsm_email: null })}
                  type="button"
                >
                  ✕
                </button>
              </span>
            </div>
          )}

          {/* Customer filter and export sit together because they are one
              workflow: narrow to the accounts you are working, then take that
              exact list to a spreadsheet. The chips the picker renders also
              replace the old raw-id chip, which read `Customer: 3f9c…` — a uuid
              on screen reads as a bug, and every row already carries the name. */}
          {/*
           * ONE ROW: the picker, the row-count note, and Export. These were
           * three stacked lines using a third of the width each — measured at
           * 57px for this block plus its own line for the note, above a table
           * bounded to whatever was left. `alignItems: center` rather than
           * flex-start so the note sits on the picker's baseline instead of
           * starting a fourth line.
           */}
          <div
            data-testid="supplier-agreements-customer-filter"
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              marginBottom: 8,
            }}
          >
            <CustomerPicker
              max={MAX_CUSTOMERS}
              nameHints={nameHints}
              onChange={setCustomerIds}
              selected={customerIds}
            />

            <div style={{ textAlign: 'right' }}>
              <button
                className="btn"
                data-testid="supplier-agreements-export"
                disabled={loading || summary.rows === 0}
                onClick={downloadCsv}
                type="button"
              >
                Export CSV
              </button>
              {/* States what the file will contain BEFORE it is downloaded, and
                  says so out loud when the fetched page is not the whole result.
                  A 500-row CSV out of 812 looks complete once it is open in
                  Excel — nothing in the file itself says it was cut off, and it
                  gets used to true up a commission or mailed to a hotel. */}
              {!loading && summary.rows > 0 && (
                <div
                  data-testid="supplier-agreements-export-note"
                  style={{
                    fontSize: 11,
                    marginTop: 4,
                    color: summary.complete
                      ? 'var(--text-secondary)'
                      : 'var(--text-primary)',
                  }}
                >
                  {summary.complete ? (
                    <>
                      {summary.rows.toLocaleString()} row
                      {summary.rows === 1 ? '' : 's'} — one per agreement. Rate
                      columns list every rate on the agreement, aligned by
                      position.
                    </>
                  ) : (
                    <strong>
                      Partial: the first {summary.agreements.toLocaleString()}{' '}
                      of {(summary.total ?? 0).toLocaleString()} agreements.
                      Narrow the filter to export them all.
                    </strong>
                  )}
                </div>
              )}
            </div>
          </div>

          {error && <div className="banner banner-error">{error}</div>}

          {!loading && !error && agreements.length === 0 ? (
            <div
              className="empty-state"
              data-testid="supplier-agreements-empty"
            >
              <div className="glyph">⌂</div>
              <div className="empty-head">
                No agreements in &ldquo;{stageLabel}&rdquo;
              </div>
              {/* Points at the ACTION, not at a data migration. The previous
                  copy told the reader to wait for the legacy rfp/rate backfill,
                  which was abandoned on 2026-07-31 — so it was advising patience
                  for something that will never happen, on the first screen
                  anyone sees. */}
              <div className="empty-sub">
                Nothing is at this stage right now. Start an agreement to add
                one, or check another stage — an agreement only appears in the
                queue that matches its current status.
              </div>
              <div style={{ marginTop: 14 }}>
                <Link
                  className="btn btn-primary"
                  data-testid="supplier-agreements-empty-new-link"
                  to="/hotel-supplier/agreements/new"
                >
                  Start an agreement
                </Link>
              </div>
            </div>
          ) : (
            <TableScroll>
              <table
                className="data-table"
                data-testid="supplier-agreements-table"
              >
                <thead>
                  <tr>
                    <DisclosureHeaderCell />
                    {columns.map((col) => {
                      const sortKey = col.sort;
                      const active = sortKey !== null && sort === sortKey;
                      return (
                        <th
                          className={
                            col.align === 'right' ? 'right' : undefined
                          }
                          key={col.key}
                        >
                          {sortKey === null ? (
                            col.label
                          ) : (
                            <button
                              className="th-sort"
                              data-sorted={active ? dir : undefined}
                              data-testid={`supplier-agreements-sort-${col.key}`}
                              onClick={() =>
                                toggleSort(sortKey, col.defaultDir)
                              }
                              type="button"
                            >
                              {col.label}
                              <span
                                aria-hidden="true"
                                className="th-sort-glyph"
                              >
                                {active ? (dir === 'asc' ? '▲' : '▼') : ''}
                              </span>
                            </button>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr className="empty">
                      <td colSpan={totalCols}>Loading…</td>
                    </tr>
                  )}
                  {agreements.flatMap((a, i) => {
                    const h = a.property;
                    const hm = a.hotelMaster;
                    const rate = a.rates[0];
                    const live = a.loads.filter(
                      (l) => l.status === 'live',
                    ).length;
                    const isOpen = expanded.isExpanded(a.id);
                    const detailId = detailRowId('agreement', a.id);
                    const zebra = zebraFor(i);

                    const cells: Record<string, ReactNode> = {
                      hotel: (
                        <>
                          <Link
                            className="row-link"
                            data-testid={`supplier-agreements-open-${a.id}`}
                            to={`/hotel-supplier/agreements/${a.id}`}
                          >
                            {h?.propertyName || '—'}
                          </Link>
                        </>
                      ),
                      city: (
                        <>
                          {/* City and state fall back to hotel_master when the
                              customer-scoped sourcing row never captured them. */}
                          {h?.propertyCity || hm?.city || '—'}
                        </>
                      ),
                      state: <>{h?.propertyState || hm?.state || '—'}</>,
                      customer: (
                        <>
                          {/* hotel_master's derived_brand is the more specific
                              answer (Hampton Inn) where the sourcing row only has
                              the family (Hilton) — brand is what loyalty and
                              preferred-status decisions key off. */}
                          {/* The customer NAME, not the id — an account number
                              tells the reader nothing about whose agreement this
                              is. The id survives as the title attribute and as the
                              fallback when a Domo-sourced row has no name, so the
                              column is never blank and the id is still reachable. */}
                          <Link
                            className="row-link"
                            data-testid={`supplier-agreement-customer-${a.id}`}
                            title={a.customerId}
                            to={`/crm/customers/${encodeURIComponent(a.customerId)}`}
                          >
                            {a.customer?.name || a.customerId}
                          </Link>
                        </>
                      ),
                      tmc: <>{a.tmc || '—'}</>,
                      status: (
                        <>
                          {/*
                           * THE CHIP IS THE CONTROL. Jeff, 2026-09-09: "can we
                           * make it so that you can just click the chip… make
                           * it clickable, then give the options in a modal
                           * (similar to how we did the log a contact flow)."
                           *
                           * `StatusControl` falls back to a plain chip for a
                           * read-only user or a closed agreement, so the column
                           * reads identically where there is nothing to do.
                           */}
                          <StatusControl
                            agreementId={a.id}
                            allowedTransitions={a.allowedTransitions ?? []}
                            onChanged={() => setReloadKey((k) => k + 1)}
                            status={a.status}
                            testId={`supplier-agreements-status-${a.id}`}
                          />
                        </>
                      ),
                      // Kept renderable although no stage lists it as a column:
                      // the cell map is keyed, so an unlisted key costs nothing,
                      // and the workflow build is expected to want it back.
                      owner: <>{a.ownerTeam?.replace(/_/g, ' ') || '—'}</>,
                      hsm: (
                        <>
                          {/* The hotel-side contact, clickable to answer the
                              question the team actually asks about them: does
                              this person cover other properties? Clicking
                              filters the list to every agreement they are on. */}
                          {a.hsmEmail
                            ? (() => {
                                // Stacie, 2026-08-11: "Hotel Contact column is a
                                // hyperlink that doesn't go anywhere."
                                //
                                // It was never broken — it filters the list to every
                                // agreement this contact is on. The bug was that it
                                // LOOKED like a link and said nothing when clicked:
                                // it wore `row-link`, the same class the Hotel column
                                // uses for a real <Link> two cells away, and the
                                // resulting chip renders above the table where a long
                                // list scrolls it out of view.
                                //
                                // So the fix is an honest affordance, not a
                                // destination: a filter-styled control that names what
                                // it does and shows when it is the active filter.
                                // Read "doesn't go anywhere" as "gave me no feedback".
                                const active = hsmEmail === a.hsmEmail;
                                const label = a.hsmName || a.hsmEmail;
                                return (
                                  <button
                                    aria-label={
                                      active
                                        ? `Clear the filter on ${label}`
                                        : `Filter to agreements for ${label}`
                                    }
                                    aria-pressed={active}
                                    className={
                                      'row-filter' + (active ? ' active' : '')
                                    }
                                    data-active={active ? 'true' : 'false'}
                                    data-testid="supplier-agreements-hsm-filter"
                                    onClick={() =>
                                      patchParams({
                                        // A second click clears it, so the control is
                                        // reversible in place rather than only via the
                                        // chip the user may not have noticed.
                                        hsm_email: active ? null : a.hsmEmail,
                                      })
                                    }
                                    title={
                                      active
                                        ? 'Filtering by this contact — click to clear'
                                        : 'Filter to every agreement for this contact'
                                    }
                                    type="button"
                                  >
                                    <span aria-hidden="true" className="glyph">
                                      ⌕
                                    </span>
                                    {label}
                                  </button>
                                );
                              })()
                            : a.hsmName || a.hotelPocName || '—'}
                          {/* The address, nested under the name and copyable.
                              Stacie, 2026-08-31: "if they could show the e-mail
                              address so that we could quickly do it, that'd be
                              great" — then, on the nested form, "yes, that would
                              be perfect."

                              COPY, not mailto. She explained why a compose link
                              is the wrong affordance: "by this point, they're
                              likely already have a thread with the point of
                              contact", so a fresh draft is the rarer case and
                              the address is what she actually wants in hand. */}
                          {(a.hsmEmail ?? a.hotelPocEmail) && (
                            <CopyableEmail
                              email={(a.hsmEmail ?? a.hotelPocEmail) as string}
                              testId={`supplier-agreement-hsm-email-${a.id}`}
                            />
                          )}
                        </>
                      ),
                      sabre: (
                        <>
                          {/* Sabre code earns a column rather than living only in
                              the detail panel: it is the identifier the team reads
                              out loud to the GDS desk, so it has to be scannable
                              down the list. The other two GDS codes are in the
                              panel. */}
                          {hm?.sabrePropertyCode || h?.gdsPropertyCode || '—'}
                        </>
                      ),
                      rate: (
                        <>
                          {rate
                            ? `${rate.currency} ${Number(rate.amount).toFixed(0)}`
                            : '—'}
                        </>
                      ),
                      term_end: (
                        <>
                          {/* Agreement-level term end where we have it. Legacy
                              backfilled rows predate that column and only carry a
                              rate window, so fall back to the earliest rate
                              expiry — which is also what the Renewal stage
                              filters on. */}
                          {(a.termEnd
                            ? fmtDate(a.termEnd)
                            : rate
                              ? fmtDate(rate.effectiveTo)
                              : null) ?? '—'}
                          {/* The countdown, so nobody does date maths to work
                              out which of these is urgent — and a DECISION DUE
                              badge inside 90 days, because a queue where every
                              row looks equally urgent is one nobody starts at
                              the top of. */}
                          {(() => {
                            const d = daysToTermEndOf(a);
                            if (d === null) return null;
                            return (
                              <span className="cell-sub">
                                {d < 0
                                  ? `lapsed ${Math.abs(d)} days ago`
                                  : `in ${d} days`}
                                {d <= RENEWAL_DECISION_DAYS && (
                                  <>
                                    {' '}
                                    <span
                                      className="badge badge-amber badge-no-dot"
                                      data-testid={`supplier-renewal-due-${a.id}`}
                                    >
                                      decision due
                                    </span>
                                  </>
                                )}
                              </span>
                            );
                          })()}
                        </>
                      ),
                      loads: (
                        <>
                          {a.loads.length === 0
                            ? '—'
                            : `${live}/${a.loads.length} live`}
                        </>
                      ),
                      // ── NEW, all three for the Loading worklist ─────────────
                      // "The owner in theory should be a person, not just the
                      // team. And the owner should be probably whoever contacted
                      // last." — Stacie, 2026-08-31. Derived, never stored, and
                      // per-AGREEMENT: the same hotel can be sourced for one
                      // customer while being loaded for another.
                      last_contact_by: (
                        <>
                          {a.lastContact ? (
                            personOf(a.lastContact.by)
                          ) : (
                            <span className="cell-sub">never contacted</span>
                          )}
                        </>
                      ),
                      // Split from the person on purpose: two columns sort and
                      // scan independently, and "who" and "when" are the two
                      // questions asked of this row separately.
                      last_contact_at: (
                        <>
                          {/*
                           * CLICK THE DATE TO LOG A CONTACT. Jeff, 2026-09-09:
                           * "Can we do something similar with the last
                           * 'contacted date' column? That'd make it WAY easier
                           * for the team to move down the list to make their
                           * contacts."
                           *
                           * Same principle as the status chip: the value you
                           * are about to change is the thing you click. Working
                           * a chase queue means logging a call on row after
                           * row, and opening a drawer for each one is the whole
                           * cost being removed.
                           *
                           * `LogContactForm` already IS a trigger plus a modal,
                           * so this is the same component, the same validation
                           * and the same audit path as the drawer's — only the
                           * trigger's clothes differ.
                           */}
                          <LogContactForm
                            agreementId={a.id}
                            // The master link, which is what the contact rolls
                            // up to. `property.hotelCanonicalId` is on the
                            // wire but not in this surface's type; hotelMaster
                            // is, and carries the same id.
                            hotelCanonicalId={a.hotelMaster?.canonicalId}
                            onSaved={() => setReloadKey((k) => k + 1)}
                            testId={`supplier-agreements-log-${a.id}`}
                            triggerClassName="row-link"
                            triggerLabel={
                              a.lastContact
                                ? (fmtDate(a.lastContact.at) ?? '—')
                                : 'never contacted'
                            }
                            triggerTitle="Log a contact against this agreement"
                          />
                        </>
                      ),
                      contact_method: (
                        <>
                          {a.lastContact
                            ? a.lastContact.method.replace(/_/g, ' ')
                            : '—'}
                        </>
                      ),
                      // "Unless it's tested correctly, I don't really need the
                      // status. I just need the sub-status. All of these in the
                      // loading tab are loading."
                      substatus: (
                        <>
                          {/*
                           * THE LOAD-STATE CHIP OPENS THE RATE AUDIT, not the
                           * status modal — and that is the point rather than a
                           * shortcut.
                           *
                           * Jeff, 2026-09-09: "for the loading tab, when we
                           * click the status chip we'll want to have the
                           * options be the options available from the rate
                           * audit results." Which is what this chip already
                           * DISPLAYS: `loadingSubstatus` is derived from the
                           * newest rate test's result, falling back to the load
                           * row's own status. So recording an audit IS how this
                           * value changes, and offering agreement statuses here
                           * would offer moves that cannot produce what the chip
                           * shows.
                           *
                           * It also carries the workflow: an "as agreed" result
                           * advances the agreement to Active on its own, which
                           * is this tab's entire job. The status modal stays
                           * available on the detail page for the manual path —
                           * an agreement verified outside the tool.
                           */}
                          <LogRateTestForm
                            agreementId={a.id}
                            onSaved={() => setReloadKey((k) => k + 1)}
                            testId={`supplier-agreements-audit-${a.id}`}
                            triggerClassName={
                              a.loadingSubstatus
                                ? substatusBadge(a.loadingSubstatus)
                                : 'cell-sub'
                            }
                            triggerLabel={
                              a.loadingSubstatus
                                ? a.loadingSubstatus.replace(/_/g, ' ')
                                : 'not tested'
                            }
                            triggerTitle="Record a rate audit — the result sets this load state"
                          />
                        </>
                      ),
                      // "Maybe they've contacted this one recently, but it was
                      // accepted a month ago. That tells me we've got to get on
                      // this hotel and escalate."
                      accepted: (
                        <>
                          {(a.acceptedAt ? fmtDate(a.acceptedAt) : null) ?? '—'}
                        </>
                      ),
                      // The two decisions a renewal row exists to capture —
                      // "dismiss or send renewal". Both are real outcomes: not
                      // every expiring rate is worth renewing, and saying so is
                      // the point of working the queue rather than renewing by
                      // default.
                      renewal_action: (
                        <RenewalDecision
                          agreementId={a.id}
                          onDone={() => setReloadKey((k) => k + 1)}
                        />
                      ),
                    };

                    const rows = [
                      <tr data-zebra={zebra} key={a.id}>
                        <DisclosureCell
                          controls={detailId}
                          label={h?.propertyName || 'this agreement'}
                          onToggle={() => expanded.toggle(a.id)}
                          open={isOpen}
                          testId={`supplier-agreements-expand-${a.id}`}
                        />

                        {columns.map((col) => (
                          <td
                            className={col.mono ? 'mono' : undefined}
                            key={col.key}
                            style={{
                              ...(col.align === 'right'
                                ? {
                                    textAlign: 'right' as const,
                                    fontWeight: 600,
                                  }
                                : {}),
                              ...(col.key === 'hotel'
                                ? { fontWeight: 600 }
                                : {}),
                              ...(col.key === 'term_end'
                                ? { color: 'var(--text-secondary)' }
                                : {}),
                            }}
                          >
                            {cells[col.key] ?? '—'}
                          </td>
                        ))}
                      </tr>,
                    ];

                    if (isOpen) {
                      rows.push(
                        <DetailRow
                          colSpan={totalCols}
                          id={detailId}
                          key={`${a.id}-detail`}
                          testId={`supplier-agreements-detail-${a.id}`}
                          zebra={zebra}
                        >
                          <AgreementDetail
                            agreement={a}
                            // A passing audit can move this row off the tab
                            // entirely, so the LIST reloads, not just the panel.
                            onAgreementMayHaveChanged={() =>
                              setReloadKey((k) => k + 1)
                            }
                          />
                        </DetailRow>,
                      );
                    }
                    return rows;
                  })}
                </tbody>
              </table>
            </TableScroll>
          )}
        </div>
      </div>
    </div>
  );
}
